import React, { useCallback, useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { ChartOfAccountDetailsEntity } from '../../../api/types/settings/accounting/chart-of-accounts/chart-of-account-details.type';
import { AccountTypeEnum } from '../../../api/types/settings/accounting/chart-of-accounts/chart-of-accounts.type';
import { getChartOfAccountsOperation } from './operations/get-chart-of-accounts-operation';
import { AddChartOfAccountDialog } from './components/add-chart-of-account-dialog';
import { EditChartOfAccountDialog } from './components/edit-chart-of-account-dialog';

const accountTypeLabel = (type: AccountTypeEnum): string => {
  switch (type) {
    case AccountTypeEnum.CASH:
      return 'מזומן';
    case AccountTypeEnum.CHECK:
      return 'שיק';
    case AccountTypeEnum.BANK:
      return 'בנק';
    case AccountTypeEnum.CREDIT_CARD:
      return 'אשראי';
    case AccountTypeEnum.INCOME:
      return 'הכנסות';
    default:
      return '';
  }
};

export const SettingsAccountingPage: React.FC = () => {
  const [accounts, setAccounts] = useState<ChartOfAccountDetailsEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editAccount, setEditAccount] =
    useState<ChartOfAccountDetailsEntity | null>(null);

  const fetchAccounts = useCallback(() => {
    void getChartOfAccountsOperation(setLoading, setAccounts);
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  return (
    <div
      style={{
        paddingTop: '24px',
        direction: 'rtl',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h2 style={{ margin: 0, textAlign: 'right' }}>הנהלת חשבונות</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{ fontSize: '14px', color: '#495057' }}>
            מספר שורות: {accounts.length}
          </label>
          <Button
            icon="pi pi-plus"
            tooltip="הוספת חשבון"
            rounded
            style={{ height: '30px', width: '30px' }}
            onClick={() => setShowAddDialog(true)}
          />
        </div>
      </div>

      <DataTable
        value={accounts}
        loading={loading}
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        dataKey="id"
        tableStyle={{ direction: 'rtl' }}
        scrollable
        scrollHeight="calc(100vh - 220px)"
      >
        <Column
          style={{ textAlign: 'right', width: '120px' }}
          field="accountCode"
          header="קוד חשבון"
        />
        <Column
          style={{ textAlign: 'right' }}
          field="accountName"
          header="שם חשבון"
        />
        <Column
          style={{ textAlign: 'right', width: '120px' }}
          field="accountType"
          header="סוג חשבון"
          body={(row: ChartOfAccountDetailsEntity) =>
            accountTypeLabel(row.accountType)
          }
        />
        <Column
          style={{ textAlign: 'right' }}
          field="creditCardCompany"
          header="חברת אשראי"
          body={(row: ChartOfAccountDetailsEntity) =>
            row.creditCardCompany || '-'
          }
        />
        <Column
          style={{ textAlign: 'right' }}
          field="bankName"
          header="בנק"
          body={(row: ChartOfAccountDetailsEntity) => row.bankName || '-'}
        />
        <Column
          style={{ textAlign: 'right' }}
          field="branches"
          header="שלוחות"
          body={(row: ChartOfAccountDetailsEntity) =>
            row.branches && row.branches.length > 0
              ? row.branches.map((b) => b.branchName).join(', ')
              : '-'
          }
        />
        <Column
          style={{ textAlign: 'right', width: '100px' }}
          field="isActive"
          header="סטטוס"
          body={(row: ChartOfAccountDetailsEntity) => (
            <Tag
              severity={row.isActive ? 'success' : 'danger'}
              value={row.isActive ? 'פעיל' : 'לא פעיל'}
            />
          )}
        />
        <Column
          style={{ textAlign: 'right', width: '70px' }}
          header=""
          body={(row: ChartOfAccountDetailsEntity) => (
            <Button
              icon="pi pi-pencil"
              text
              tooltip="עריכה"
              tooltipOptions={{ position: 'top' }}
              onClick={() => setEditAccount(row)}
            />
          )}
        />
      </DataTable>

      <AddChartOfAccountDialog
        visible={showAddDialog}
        onHide={() => setShowAddDialog(false)}
        onCreated={fetchAccounts}
      />

      <EditChartOfAccountDialog
        visible={!!editAccount}
        account={editAccount}
        onHide={() => setEditAccount(null)}
        onUpdated={fetchAccounts}
      />
    </div>
  );
};

export default SettingsAccountingPage;
